import { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Layout, Form, DatePicker, Radio, InputNumber, Input, Button, message, Space } from 'antd';
import RichEditor, { isEffectivelyEmptyRichHtml } from '@components/common/RichEditor';
import PageHeader from '@components/PageHeader/PageHeader';

import { SelectionFormItemLayout } from '../../../../utils/property';
import dayjs from 'dayjs';
import { getProjestsApi, updataVoteSetting } from '@services/Selection';
import { fileApi } from '@services/Conference';
import config from '@config';
import { handleVoteSettingDefaultValue, option, handleForm2Params, keys } from './util';
import { workspaceSelectionVotePath } from '@/layouts/selectionWorkspacePaths';

import './index.less';
const Settings = () => {
    const params = useParams();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const local = useLocation();
    const [loading, setLoading] = useState(false);
    const [projectInfo, setProjectInfo] = useState(null);

    useEffect(() => {
        document.title = '投票规则设置-医评议';
    }, []);

    useEffect(() => {
        if (params.pid && !projectInfo) {
            getProjestsApi({ id: params.pid }).then(res => {
                if (res.status_code === 200) {
                    setProjectInfo(res.data);
                }
            });
        }
    }, [params.pid]);

    const [currentValues, setCurrentValues] = useState(() => handleVoteSettingDefaultValue(local.state));

    const rule = Form.useWatch('rule', form);

    const imageUploadHandler = useCallback(async blobInfo => {
        const formData = new FormData();
        formData.append('file', blobInfo.blob());
        formData.append('type', 13);
        const hide = message.loading('上传中...', 0);
        try {
            const res = await fileApi(formData, option);
            const raw = res.data;
            let url = raw;
            if (raw && raw.includes('https')) {
                url = raw;
            } else if (raw && !String(raw).includes('https')) {
                url = config.OSS_PREFIX + raw;
            }
            hide();
            return url;
        } catch (e) {
            hide();
            throw e;
        }
    }, []);

    const handleCancel = () => {
        navigate(-1);
    };

    const handleSubmit = () => {
        form.validateFields().then(res => {
            const topicVoteSettings = handleForm2Params(res);
            setLoading(true);

            updataVoteSetting({ topicVoteSettings: JSON.stringify(topicVoteSettings), id: params.tid }).then(res => {
                setLoading(false);
                if (res.status_code === 200) {
                    message.success('设置成功！');
                    handleCancel();
                }
            });
        });
    };

    const onValuesChange = v => {
        if (keys.includes(Object.keys(v)[0])) {
            setCurrentValues(d => ({ ...d, ...v }));
        }
    };

    return (
        <>
            <PageHeader
                title={
                    <>
                        <i className="bi bi-chevron-left page-header-back-btn" onClick={() => navigate(-1)} />
                        <span>投票规则设置</span>
                    </>
                }
                breadcrumb={[{ label: '医评议', path: '/selection/projects' }, { label: projectInfo?.name }, '投票规则设置']}
            ></PageHeader>

            <div className="page-route-body scoring-setting-content">
                <Form form={form} name="time" {...SelectionFormItemLayout} initialValues={currentValues} onValuesChange={onValuesChange}>
                    <h2>投票起止时间</h2>
                    <Form.Item label="投票起止时间" name="time" rules={[{ required: true, message: '请选择' }]}>
                        <DatePicker.RangePicker
                            style={{ width: 400 }}
                            showTime={{
                                hideDisabledOptions: true,
                                defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('11:59', 'HH:mm')],
                            }}
                            format="YYYY-MM-DD HH:mm"
                        />
                    </Form.Item>

                    <h2>投票阶段提醒</h2>
                    <Form.Item label="提醒弹窗" name="rule" rules={[{ required: true, message: '请选择' }]}>
                        <Radio.Group>
                            <Radio value={0}>不提醒</Radio>
                            <Radio value={1}>每次进入提醒</Radio>
                            <Radio value={2}>仅提醒一次</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        name="message"
                        label="提醒内容"
                        rules={[
                            {
                                required: rule !== 0,
                                message: '请输入',
                            },
                            {
                                validator: async (_, value) => {
                                    if (isEffectivelyEmptyRichHtml(value)) {
                                        throw new Error('请输入');
                                    }
                                },
                            },
                        ]}
                    >
                        <RichEditor id="vote-setting-message" mode="full" imageUploadHandler={imageUploadHandler} className="react-editor" />
                    </Form.Item>

                    <h2>职务认证用户每次投票票数</h2>
                    <Form.Item label="注册用户" name="reggedUserTicket" rules={[{ required: true, message: '请选择' }]} className="form-group-item">
                        <Radio.Group>
                            <Radio value={1}>可投票</Radio>
                            <Radio value={0}>不可投票</Radio>
                            <Form.Item label="每次投票加" name="reggedUserTicketCount" style={{ opacity: currentValues.reggedUserTicket }}>
                                <Space.Compact>
                                    <InputNumber style={{ width: 72 }} min={1} precision={0} />
                                    <Input readOnly style={{ width: 36, textAlign: 'center' }} tabIndex={-1} value="票" />
                                </Space.Compact>
                            </Form.Item>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="医疗机构从业者" name="A01" rules={[{ required: true, message: '请选择' }]} className="form-group-item">
                        <Radio.Group>
                            <Radio value={1}>可投票</Radio>
                            <Radio value={0}>不可投票</Radio>
                            <Form.Item label="每次投票加" name="A01Count" style={{ opacity: currentValues.A01 }}>
                                <Space.Compact>
                                    <InputNumber style={{ width: 72 }} min={1} precision={0} />
                                    <Input readOnly style={{ width: 36, textAlign: 'center' }} tabIndex={-1} value="票" />
                                </Space.Compact>
                            </Form.Item>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="社会团体人士" name="A06" rules={[{ required: true, message: '请选择' }]} className="form-group-item">
                        <Radio.Group>
                            <Radio value={1}>可投票</Radio>
                            <Radio value={0}>不可投票</Radio>
                            <Form.Item label="每次投票加" name="A06Count" style={{ opacity: currentValues.A06 }}>
                                <Space.Compact>
                                    <InputNumber style={{ width: 72 }} min={1} precision={0} />
                                    <Input readOnly style={{ width: 36, textAlign: 'center' }} tabIndex={-1} value="票" />
                                </Space.Compact>
                            </Form.Item>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="政府官员" name="A02" rules={[{ required: true, message: '请选择' }]} className="form-group-item">
                        <Radio.Group>
                            <Radio value={1}>可投票</Radio>
                            <Radio value={0}>不可投票</Radio>
                            <Form.Item label="每次投票加" name="A02Count" style={{ opacity: currentValues.A02 }}>
                                <Space.Compact>
                                    <InputNumber style={{ width: 72 }} min={1} precision={0} />
                                    <Input readOnly style={{ width: 36, textAlign: 'center' }} tabIndex={-1} value="票" />
                                </Space.Compact>
                            </Form.Item>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="教育科研人士" name="A04" rules={[{ required: true, message: '请选择' }]} className="form-group-item">
                        <Radio.Group>
                            <Radio value={1}>可投票</Radio>
                            <Radio value={0}>不可投票</Radio>
                            <Form.Item label="每次投票加" name="A04Count" style={{ opacity: currentValues.A04 }}>
                                <Space.Compact>
                                    <InputNumber style={{ width: 72 }} min={1} precision={0} />
                                    <Input readOnly style={{ width: 36, textAlign: 'center' }} tabIndex={-1} value="票" />
                                </Space.Compact>
                            </Form.Item>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="商业人士" name="A03" rules={[{ required: true, message: '请选择' }]} className="form-group-item">
                        <Radio.Group>
                            <Radio value={1}>可投票</Radio>
                            <Radio value={0}>不可投票</Radio>
                            <Form.Item label="每次投票加" name="A03Count" style={{ opacity: currentValues.A03 }}>
                                <Space.Compact>
                                    <InputNumber style={{ width: 72 }} min={1} precision={0} />
                                    <Input readOnly style={{ width: 36, textAlign: 'center' }} tabIndex={-1} value="票" />
                                </Space.Compact>
                            </Form.Item>
                        </Radio.Group>
                    </Form.Item>

                    <h2>机构认证每次投票票数</h2>
                    <Form.Item label="医疗机构" name="2" rules={[{ required: true, message: '请选择' }]} className="form-group-item">
                        <Radio.Group>
                            <Radio value={1}>可投票</Radio>
                            <Radio value={0}>不可投票</Radio>
                            <Form.Item label="每次投票加" name="2Count" style={{ opacity: currentValues[2] }}>
                                <Space.Compact>
                                    <InputNumber style={{ width: 72 }} min={1} precision={0} />
                                    <Input readOnly style={{ width: 36, textAlign: 'center' }} tabIndex={-1} value="票" />
                                </Space.Compact>
                            </Form.Item>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="企业" name="0" rules={[{ required: true, message: '请选择' }]} className="form-group-item">
                        <Radio.Group>
                            <Radio value={1}>可投票</Radio>
                            <Radio value={0}>不可投票</Radio>
                            <Form.Item label="每次投票加" name="0Count" style={{ opacity: currentValues[0] }}>
                                <Space.Compact>
                                    <InputNumber style={{ width: 72 }} min={1} precision={0} />
                                    <Input readOnly style={{ width: 36, textAlign: 'center' }} tabIndex={-1} value="票" />
                                </Space.Compact>
                            </Form.Item>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="媒体" name="1" rules={[{ required: true, message: '请选择' }]} className="form-group-item">
                        <Radio.Group>
                            <Radio value={1}>可投票</Radio>
                            <Radio value={0}>不可投票</Radio>
                            <Form.Item label="每次投票加" name="1Count" style={{ opacity: currentValues[1] }}>
                                <Space.Compact>
                                    <InputNumber style={{ width: 72 }} min={1} precision={0} />
                                    <Input readOnly style={{ width: 36, textAlign: 'center' }} tabIndex={-1} value="票" />
                                </Space.Compact>
                            </Form.Item>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="机构" name="3" rules={[{ required: true, message: '请选择' }]} className="form-group-item">
                        <Radio.Group>
                            <Radio value={1}>可投票</Radio>
                            <Radio value={0}>不可投票</Radio>
                            <Form.Item label="每次投票加" name="3Count" style={{ opacity: currentValues[3] }}>
                                <Space.Compact>
                                    <InputNumber style={{ width: 72 }} min={1} precision={0} />
                                    <Input readOnly style={{ width: 36, textAlign: 'center' }} tabIndex={-1} value="票" />
                                </Space.Compact>
                            </Form.Item>
                        </Radio.Group>
                    </Form.Item>

                    <h2>投票阶段展示设置</h2>
                    <Form.Item label="投票按钮" name="visibleVoteButton" rules={[{ required: true, message: '请选择' }]}>
                        <Radio.Group>
                            <Radio value={1}>展示</Radio>
                            <Radio value={0}>隐藏</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="实践票数" name="visibleVoteNum" rules={[{ required: true, message: '请选择' }]}>
                        <Radio.Group>
                            <Radio value={1}>展示</Radio>
                            <Radio value={0}>隐藏</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="实践投票排行榜" name="visibleRank" rules={[{ required: true, message: '请选择' }]}>
                        <Radio.Group>
                            <Radio value={1}>展示</Radio>
                            <Radio value={0}>隐藏</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item label="投票排行实践个数" name="rankCaseNum" rules={[{ required: true, message: '请输入' }]} extra="同票实践显示相同排名，顺位实践会进位展示排名；人气排行榜按设定的上榜实践个数展示，设定范围10-20；">
                        <Space.Compact>
                            <Input readOnly style={{ width: 96, textAlign: 'center' }} tabIndex={-1} value="当前主题前" />
                            <InputNumber style={{ width: 120 }} min={1} precision={0} />
                            <Input readOnly style={{ width: 200, textAlign: 'center' }} tabIndex={-1} value="个实践展示在结果上" />
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item label="投票完整结果查看限制" name="isAuth" rules={[{ required: true, message: '请选择' }]} extra="开启完整结果查看限制，未认证用户可查看结果前3名实践">
                        <Radio.Group>
                            <Radio value={1}>认证可看</Radio>
                            <Radio value={0}>无需认证</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Form>

                <div className="page-form-foot-operations">
                    <Space size="large">
                        <Button type="primary" size="large" onClick={handleSubmit} loading={loading}>
                            保存
                        </Button>

                        <Button type="default" size="large" onClick={handleCancel}>
                            取消
                        </Button>
                    </Space>
                </div>
            </div>
        </>
    );
};

export default Settings;
